import React from 'react';
import {ConvertedNumberField, FormBlock, FormSection} from "components/Form/Form";
import {MediaField, MultiSelectField, NumberField, TextAreaField, TextField} from "components/Form/Form/Field";
import {TagIcon} from "@heroicons/react/solid";
import {AnyMedia} from "types/MediaFile";
import {toTitleCase} from "services/string";
import IconName from "types/IconName";
import TrailType from "../../../types/TrailType";
import AdvisoryListField from "./AdvisoryListField";
import {TrailAdvisory} from "../../../types/TrailAdvisory";
import FieldHeader from "../../../components/Form/Form/Field/FieldHeader";
import {useOperator} from "../../../context/OperatorContext";
import {UnitSystem} from "../../../types/UnitSystem";
import {Offer} from "../../../types/Offer";
import Label from "../../../components/Form/Label";
import {ENTITY_TYPES} from "../../../components/EntitySelector/EntitySelector.constants";
import {EntitySelector} from "../../../components/EntitySelector";
import {FormikContextType, useField, useFormikContext} from "formik";
import {SelectedItemsData} from "../../../components/EntitySelector/EntitySelector.types";
import {TrailFormData} from "../../../types/Trail";
import {createSelectedWaypointEntities} from "./TrailForm.helpers";

// Helpers ----

const offerOptions = (offers: Array<Offer>) => offers.map(offer => ({
  id: offer.id,
  name: `${toTitleCase(offer.name)} (${offer.incentive})`,
  icon: IconName.GIFT
}))

// Component ----

export type WaypointEditorProps = {
  name: string
  index: number
  trailType?: TrailType
  trailIsCircuit?: boolean
  media?: Array<AnyMedia>
  offers?: Array<Offer>
  advisories?: Array<TrailAdvisory>
}

const handleSelectEntities = (items: SelectedItemsData, name: string, index: number, formik: FormikContextType<TrailFormData>) => {
  const exhibits = items.exhibit;
  const facilities = items.facility;

  formik.setFieldValue(`${name}.${index}.exhibits`, exhibits).then(_ => {
    // Validate the form field
    formik.validateField(name).then(_ => {
      void formik.setFieldTouched(name, true);
    });
  }).catch(console.error);

  formik.setFieldValue(`${name}.${index}.facilities`, facilities).then(_ => {
    // Validate the form field
    formik.validateField(name).then(_ => {
      void formik.setFieldTouched(name, true);
    });
  }).catch(console.error);
};

function WaypointEditor({name, index, trailType, trailIsCircuit, media, offers, advisories}: WaypointEditorProps) {
  const {settings} = useOperator();
  const [field] = useField(name);
  const form = useFormikContext<TrailFormData>();

  const initialSelectedEntities = createSelectedWaypointEntities(field.value?.[index] ?? null);

  return (
    <FormSection columns={6} isTop>
      {/* Name */}
      <FormBlock columns={3}>
        <TextField
          name={`${name}.${index}.name`}
          label="Name *"
          icon={TagIcon}
          help="Give this waypoint a short, recognisable name"
          maxLength={250}
          autoComplete="off"
        />
      </FormBlock>

      {/* Description */}
      <FormBlock columns={6}>
        <TextAreaField
          name={`${name}.${index}.description`}
          label="Description"
          hint="Optional. 2,000 chars max."
          help="Briefly describe this waypoint, in a short, snappy way"
          autoComplete="off"
          maxChars={2000}
          rows={6}
        />
      </FormBlock>

      {/* Elevation */}
      <FormBlock columns={1}>
        <ConvertedNumberField
          name={`${name}.${index}.elevation`}
          label="Elevation"
          help={settings?.localisation.length_units === UnitSystem.METRIC? 'Meters' : 'Feet'}
          step={1}
          inputUnit={settings?.localisation.length_units === UnitSystem.METRIC? 'meters' : 'feet'}
          fieldUnit={'meters'}
          autoComplete="off"
        />
      </FormBlock>

      {/* Distance */}
      {(trailIsCircuit || index !== 0) &&
        <FormBlock columns={1}>
          <ConvertedNumberField
            name={`${name}.${index}.distance`}
            label="Distance"
            hint="From previous"
            help={settings?.localisation.length_units === UnitSystem.METRIC? 'Meters' : 'Feet'}
            step={1}
            inputUnit={settings?.localisation.length_units === UnitSystem.METRIC? 'meters' : 'feet'}
            fieldUnit={'meters'}
            autoComplete="off"
          />
        </FormBlock>
      }

      {trailType === TrailType.GOLF &&
        <FormBlock columns={1}>
          <NumberField
            name={`${name}.${index}.par`}
            label="Par"
            hint="Between 1 and 12"
            step={1}
            autoComplete="off"
          />
        </FormBlock>
      }

      {/* Media */}
      <FormBlock columns={6}>
        <MediaField
          name={`${name}.${index}.media_ids`}
          label="Media"
          files={media}
          maxFiles={20}
          type={[MediaField.types.AUDIO, MediaField.types.IMAGE, MediaField.types.VIDEO]}
        />
      </FormBlock>

        <div className="flex flex-col gap-2 col-span-1 sm:col-span-6 md:col-span-8 lg:col-span-6 xl:col-span-3 mt-1">
          <Label>Entities</Label>
          <EntitySelector
            onChange={(items) => handleSelectEntities(items, name, index, form)}
            buttonText={'Choose entities'}
            modalTitle='Choose entities on the waypoint'
            initialSelectedItems={initialSelectedEntities}
            entityTypes={[ENTITY_TYPES.EXHIBIT, ENTITY_TYPES.FACILITY]}
          />
        </div>

      <FormBlock columns={6} />

      {/* Offers */}
      <FormBlock columns={6} extraLargeColumns={3}>
        <MultiSelectField
          name={`${name}.${index}.offer_ids`}
          label="Offer"
          hint='Choose a maximum of 1'
          options={offerOptions(offers ?? [])}
          max={1}
        />
      </FormBlock>

      {/* Advisories */}
      <FormBlock columns={6}>
        <FieldHeader fieldId={`${name}.${index}.advisories`} label='Advisories'/>
        <div className="mt-2">
          <AdvisoryListField name={`${name}.${index}.advisories`} advisories={advisories ?? []} dark/>
        </div>
      </FormBlock>
    </FormSection>
  );
}

export default WaypointEditor;